/**
 * Staff worklist of reservations: copies to take off the shelves, and the hold shelf.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type { PreparationList, ReadyForPickupResult } from "@/api/types";

const KEY = "reservation-preparation";

export function useReservationPreparation(pickupLocationId: string) {
  return useQuery({
    queryKey: [KEY, pickupLocationId],
    queryFn: () =>
      api.get<PreparationList>(
        `/circulation/reservation-preparation${
          pickupLocationId ? `?pickupLocationId=${encodeURIComponent(pickupLocationId)}` : ""
        }`
      ),
    // New reservations and returns keep coming in at the desk
    refetchInterval: 60_000,
  });
}

function useRefresh() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: [KEY] });
    void queryClient.invalidateQueries({ queryKey: ["patrons"] });
  };
}

export function useMarkReservationReady() {
  const refresh = useRefresh();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<ReadyForPickupResult>(`/circulation/reservation-preparation/${id}/ready`),
    onSuccess: refresh,
  });
}

export function useShelveScannedReservation() {
  const refresh = useRefresh();

  return useMutation({
    mutationFn: (input: { barcode: string; pickupLocationId?: string }) =>
      api.post<ReadyForPickupResult>("/circulation/reservation-preparation/scan", input),
    onSuccess: refresh,
  });
}
